import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaFilePdf, FaPrint } from 'react-icons/fa';
import { getAuditLogDetails } from '../../../redux/Slices/AuditLogSlice';
import { useTranslation } from 'react-i18next';
import { useTitle } from '../../../shared/hooks/TitleContext';
import i18n from '../../../i18n/i18n';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const ViewAuditLog = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { setTitle } = useTitle();
  const { t } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';

  const [auditLog, setAuditLog] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  useEffect(() => {
    setTitle('auditLogs.viewTitle', { defaultValue: 'عرض تفاصيل السجل' });
  }, [setTitle]);

  useEffect(() => {
    const fetchAuditLog = async () => {
      setIsLoading(true);
      try {
        const result = await dispatch(getAuditLogDetails({ id, language: i18n.language }));
        if (getAuditLogDetails.fulfilled.match(result)) {
          const data = result.payload?.data || result.payload;
          setAuditLog(data);
        } else {
          toast.error(result.payload || t('auditLogs.errors.fetchError', { defaultValue: 'فشل في جلب السجل' }), { rtl: isRTL });
          navigate('/audit-logs');
        }
      } catch (error) {
        toast.error(t('auditLogs.errors.fetchError', { defaultValue: 'فشل في جلب السجل' }), { rtl: isRTL });
        navigate('/audit-logs');
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchAuditLog();
    }
  }, [id, dispatch, navigate, t, isRTL, i18n.language]);

  const handlePrintPDF = async () => {
    if (!auditLog) return;
    
    setIsGeneratingPdf(true);
    try {
      const element = document.getElementById('audit-log-pdf');
      if (!element) {
        toast.error(t('auditLogs.errors.printError', { defaultValue: 'فشل في طباعة السجل' }), { rtl: isRTL });
        return;
      }

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`audit-log-${id}-${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success(t('auditLogs.success.printed', { defaultValue: 'تم طباعة السجل بنجاح' }), { rtl: isRTL });
    } catch (error) {
      toast.error(t('auditLogs.errors.printError', { defaultValue: 'فشل في طباعة السجل' }), { rtl: isRTL });
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getEventLabel = (eventType) => {
    const labels = {
      created: t('auditLogs.created'),
      updated: t('auditLogs.updated'),
      deleted: t('auditLogs.deleted'),
      restored: t('auditLogs.restored'),
    };
    return labels[eventType] || eventType;
  };

  const formatJson = (obj) => {
    if (!obj) return '—';
    if (typeof obj === 'string') {
      try {
        return JSON.stringify(JSON.parse(obj), null, 2);
      } catch {
        return obj;
      }
    }
    return JSON.stringify(obj, null, 2);
  };

  if (isLoading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#ccc' }}>
        {t('auditLogs.loading', { defaultValue: 'جاري التحميل...' })}
      </div>
    );
  }

  if (!auditLog) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#ccc' }}>
        {t('auditLogs.notFound', { defaultValue: 'السجل غير موجود' })}
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', direction: dir, textAlign }}>
      <div style={{ 
        backgroundColor: '#1b3350', 
        borderRadius: '12px', 
        padding: '24px',
        border: '1px solid #333'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ color: 'white', margin: 0 }}>
            {t('auditLogs.viewTitle', { defaultValue: 'عرض تفاصيل السجل' })} #{auditLog.id}
          </h2>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => navigate('/audit-logs')}
              style={{
                backgroundColor: '#666',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <FaArrowLeft /> {t('auditLogs.back', { defaultValue: 'رجوع' })}
            </button>
            <button
              onClick={handlePrintPDF}
              disabled={isGeneratingPdf}
              style={{
                backgroundColor: isGeneratingPdf ? '#666' : '#dc3545',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '8px',
                cursor: isGeneratingPdf ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <FaFilePdf /> {isGeneratingPdf ? t('auditLogs.generating', { defaultValue: 'جاري التوليد...' }) : t('auditLogs.printPDF', { defaultValue: 'طباعة PDF' })}
            </button>
          </div>
        </div>

        <div style={{ 
          backgroundColor: '#1a1f2e', 
          padding: '20px', 
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
            <div>
              <label style={{ color: '#999', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                {t('auditLogs.id', { defaultValue: 'ID' })}
              </label>
              <div style={{ color: 'white', fontSize: '16px' }}>{auditLog.id}</div>
            </div>
            <div>
              <label style={{ color: '#999', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                {t('auditLogs.user', { defaultValue: 'المستخدم' })}
              </label>
              <div style={{ color: 'white', fontSize: '16px' }}>
                {auditLog.user?.name || auditLog.user?.email || (auditLog.user_id ? `User #${auditLog.user_id}` : '—')}
              </div>
            </div>
            <div>
              <label style={{ color: '#999', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                {t('auditLogs.auditableType', { defaultValue: 'نوع النموذج' })}
              </label>
              <div style={{ color: 'white', fontSize: '16px' }}>{auditLog.auditable_type || auditLog.model_type || '—'}</div>
            </div>
            <div>
              <label style={{ color: '#999', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                {t('auditLogs.auditableId', { defaultValue: 'معرف السجل' })}
              </label>
              <div style={{ color: 'white', fontSize: '16px' }}>{auditLog.auditable_id || auditLog.model_id || '—'}</div>
            </div>
            <div>
              <label style={{ color: '#999', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                {t('auditLogs.event', { defaultValue: 'الحدث' })}
              </label>
              <div style={{ color: 'white', fontSize: '16px' }}>{getEventLabel(auditLog.event)}</div>
            </div>
            <div>
              <label style={{ color: '#999', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                {t('auditLogs.ipAddress', { defaultValue: 'عنوان IP' })}
              </label>
              <div style={{ color: 'white', fontSize: '16px' }}>{auditLog.ip_address || '—'}</div>
            </div>
            <div>
              <label style={{ color: '#999', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                {t('auditLogs.userAgent', { defaultValue: 'المتصفح' })}
              </label>
              <div style={{ color: 'white', fontSize: '14px', wordBreak: 'break-word' }}>{auditLog.user_agent || '—'}</div>
            </div>
            <div>
              <label style={{ color: '#999', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                {t('auditLogs.url', { defaultValue: 'الرابط' })}
              </label>
              <div style={{ color: 'white', fontSize: '14px', wordBreak: 'break-word' }}>{auditLog.url || '—'}</div>
            </div>
            <div>
              <label style={{ color: '#999', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                {t('auditLogs.tags', { defaultValue: 'العلامات' })}
              </label>
              <div style={{ color: 'white', fontSize: '16px' }}>
                {auditLog.tags ? (Array.isArray(auditLog.tags) ? auditLog.tags.join(', ') : String(auditLog.tags)) : '—'}
              </div>
            </div>
            <div>
              <label style={{ color: '#999', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                {t('auditLogs.createdAt', { defaultValue: 'تاريخ الإنشاء' })}
              </label>
              <div style={{ color: 'white', fontSize: '16px' }}>{formatDate(auditLog.created_at)}</div>
            </div>
          </div>
        </div>

        {(auditLog.old_values || auditLog.new_values) && (
          <div style={{ 
            backgroundColor: '#1a1f2e', 
            padding: '20px', 
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <h3 style={{ color: 'white', marginBottom: '20px' }}>
              {t('auditLogs.changes', { defaultValue: 'التغييرات' })}
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              {auditLog.old_values && (
                <div>
                  <label style={{ color: '#ff6b6b', fontSize: '16px', display: 'block', marginBottom: '15px', fontWeight: 'bold' }}>
                    {t('auditLogs.oldValues', { defaultValue: 'القيم القديمة' })}
                  </label>
                  <div style={{ 
                    backgroundColor: '#14263b', 
                    padding: '15px', 
                    borderRadius: '6px',
                    border: '1px solid #ff6b6b40'
                  }}>
                    {Object.entries(auditLog.old_values).map(([key, value]) => (
                      <div key={key} style={{ 
                        marginBottom: '12px',
                        paddingBottom: '12px',
                        borderBottom: '1px solid #333'
                      }}>
                        <div style={{ color: '#999', fontSize: '12px', marginBottom: '5px' }}>
                          {key}
                        </div>
                        <div style={{ 
                          color: '#ff6b6b', 
                          fontSize: '14px',
                          fontWeight: '500',
                          wordBreak: 'break-word'
                        }}>
                          {value !== null && value !== undefined 
                            ? (typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value))
                            : '—'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {auditLog.new_values && (
                <div>
                  <label style={{ color: '#51cf66', fontSize: '16px', display: 'block', marginBottom: '15px', fontWeight: 'bold' }}>
                    {t('auditLogs.newValues', { defaultValue: 'القيم الجديدة' })}
                  </label>
                  <div style={{ 
                    backgroundColor: '#14263b', 
                    padding: '15px', 
                    borderRadius: '6px',
                    border: '1px solid #51cf6640'
                  }}>
                    {Object.entries(auditLog.new_values).map(([key, value]) => (
                      <div key={key} style={{ 
                        marginBottom: '12px',
                        paddingBottom: '12px',
                        borderBottom: '1px solid #333'
                      }}>
                        <div style={{ color: '#999', fontSize: '12px', marginBottom: '5px' }}>
                          {key}
                        </div>
                        <div style={{ 
                          color: '#51cf66', 
                          fontSize: '14px',
                          fontWeight: '500',
                          wordBreak: 'break-word'
                        }}>
                          {value !== null && value !== undefined 
                            ? (typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value))
                            : '—'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Hidden PDF Template */}
        <div
          id="audit-log-pdf"
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: '-9999px',
            top: 0,
            width: '210mm',
            minHeight: '297mm',
            backgroundColor: '#ffffff',
            color: '#000000',
            padding: '20mm',
            fontFamily: 'Arial, sans-serif',
            fontSize: '12px'
          }}
        >
          <h1 style={{ fontSize: '24px', marginBottom: '20px', textAlign: 'center' }}>
            {t('auditLogs.viewTitle', { defaultValue: 'عرض تفاصيل السجل' })} #{auditLog.id}
          </h1>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
            <tr>
              <td style={{ padding: '8px', border: '1px solid #000', fontWeight: 'bold' }}>ID</td>
              <td style={{ padding: '8px', border: '1px solid #000' }}>{auditLog.id}</td>
            </tr>
            <tr>
              <td style={{ padding: '8px', border: '1px solid #000', fontWeight: 'bold' }}>{t('auditLogs.user', { defaultValue: 'المستخدم' })}</td>
              <td style={{ padding: '8px', border: '1px solid #000' }}>{auditLog.user?.name || auditLog.user?.email || (auditLog.user_id ? `User #${auditLog.user_id}` : '—')}</td>
            </tr>
            <tr>
              <td style={{ padding: '8px', border: '1px solid #000', fontWeight: 'bold' }}>{t('auditLogs.auditableType', { defaultValue: 'نوع النموذج' })}</td>
              <td style={{ padding: '8px', border: '1px solid #000' }}>{auditLog.auditable_type || auditLog.model_type || '—'}</td>
            </tr>
            <tr>
              <td style={{ padding: '8px', border: '1px solid #000', fontWeight: 'bold' }}>{t('auditLogs.auditableId', { defaultValue: 'معرف السجل' })}</td>
              <td style={{ padding: '8px', border: '1px solid #000' }}>{auditLog.auditable_id || auditLog.model_id || '—'}</td>
            </tr>
            <tr>
              <td style={{ padding: '8px', border: '1px solid #000', fontWeight: 'bold' }}>{t('auditLogs.event', { defaultValue: 'الحدث' })}</td>
              <td style={{ padding: '8px', border: '1px solid #000' }}>{getEventLabel(auditLog.event)}</td>
            </tr>
            <tr>
              <td style={{ padding: '8px', border: '1px solid #000', fontWeight: 'bold' }}>{t('auditLogs.ipAddress', { defaultValue: 'عنوان IP' })}</td>
              <td style={{ padding: '8px', border: '1px solid #000' }}>{auditLog.ip_address || '—'}</td>
            </tr>
            <tr>
              <td style={{ padding: '8px', border: '1px solid #000', fontWeight: 'bold' }}>{t('auditLogs.userAgent', { defaultValue: 'المتصفح' })}</td>
              <td style={{ padding: '8px', border: '1px solid #000', fontSize: '10px' }}>{auditLog.user_agent || '—'}</td>
            </tr>
            <tr>
              <td style={{ padding: '8px', border: '1px solid #000', fontWeight: 'bold' }}>{t('auditLogs.url', { defaultValue: 'الرابط' })}</td>
              <td style={{ padding: '8px', border: '1px solid #000', fontSize: '10px' }}>{auditLog.url || '—'}</td>
            </tr>
            <tr>
              <td style={{ padding: '8px', border: '1px solid #000', fontWeight: 'bold' }}>{t('auditLogs.tags', { defaultValue: 'العلامات' })}</td>
              <td style={{ padding: '8px', border: '1px solid #000' }}>{auditLog.tags ? (Array.isArray(auditLog.tags) ? auditLog.tags.join(', ') : String(auditLog.tags)) : '—'}</td>
            </tr>
            <tr>
              <td style={{ padding: '8px', border: '1px solid #000', fontWeight: 'bold' }}>{t('auditLogs.createdAt', { defaultValue: 'تاريخ الإنشاء' })}</td>
              <td style={{ padding: '8px', border: '1px solid #000' }}>{formatDate(auditLog.created_at)}</td>
            </tr>
          </table>
          {(auditLog.old_values || auditLog.new_values) && (
            <div style={{ marginTop: '20px' }}>
              <h2 style={{ fontSize: '18px', marginBottom: '10px' }}>{t('auditLogs.changes', { defaultValue: 'التغييرات' })}</h2>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                {auditLog.old_values && (
                  <div>
                    <h3 style={{ fontSize: '14px', marginBottom: '10px' }}>{t('auditLogs.oldValues', { defaultValue: 'القيم القديمة' })}</h3>
                    <pre style={{ fontSize: '10px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {formatJson(auditLog.old_values)}
                    </pre>
                  </div>
                )}
                {auditLog.new_values && (
                  <div>
                    <h3 style={{ fontSize: '14px', marginBottom: '10px' }}>{t('auditLogs.newValues', { defaultValue: 'القيم الجديدة' })}</h3>
                    <pre style={{ fontSize: '10px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {formatJson(auditLog.new_values)}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewAuditLog;
